import type { PropType } from 'vue';
import type { Nullable } from '../../types';
import type { TTextAreaProps } from './TTextArea.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, nextTick, ref, watch } from 'vue';
import { useStyles } from './TTextArea.styles';

const DEFAULT_AUTO_RESIZE_ROWS = 1;

export const TTextArea = defineComponent({
  name: 'TTextArea',
  emits: ['input', 'change', 'update:modelValue', 'focus', 'blur'],
  props: {
    id: String as PropType<TTextAreaProps['id']>,
    value: String as PropType<TTextAreaProps['value']>,
    modelValue: String as PropType<TTextAreaProps['modelValue']>,
    label: String as PropType<TTextAreaProps['label']>,
    error: Boolean as PropType<TTextAreaProps['error']>,
    disabled: Boolean as PropType<TTextAreaProps['disabled']>,
    placeholder: String as PropType<TTextAreaProps['placeholder']>,
    helperText: String as PropType<TTextAreaProps['helperText']>,
    fullWidth: Boolean as PropType<TTextAreaProps['fullWidth']>,
    maxlength: Number as PropType<TTextAreaProps['maxlength']>,
    autoResize: Boolean as PropType<TTextAreaProps['autoResize']>,
    maxRows: {
      type: Number as PropType<TTextAreaProps['maxRows']>,
      default: undefined,
    },
  },
  setup(_props, { attrs, emit, slots, expose }) {
    const props = computed(() => merge({}, _props, attrs));
    const baseClass = useStyles();

    const internalValue = computed({
      get: () => (props.value.modelValue !== undefined
        ? props.value.modelValue
        : props.value.value),
      set: (newValue) => {
        emit('update:modelValue', newValue);
        emit('input', newValue);
        emit('change', newValue);
      },
    });

    const resizeTextarea = () => {
      const el = textarea.value;
      if (!el || !props.value.autoResize) {
        return;
      }
      el.style.height = 'auto';
      const styles = getComputedStyle(el);
      const borderTop = Number.parseFloat(styles.borderTopWidth);
      const borderBottom = Number.parseFloat(styles.borderBottomWidth);
      const maxRows = props.value.maxRows;
      if (maxRows) {
        const lineHeight = Number.parseFloat(styles.lineHeight);
        const paddingTop = Number.parseFloat(styles.paddingTop);
        const paddingBottom = Number.parseFloat(styles.paddingBottom);
        const maxHeight
          = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;
        el.style.maxHeight = `${maxHeight}px`;
      }
      el.style.height = `${el.scrollHeight + borderTop + borderBottom}px`;
    };

    const handleInput = (event: Event) => {
      internalValue.value = (event.target as HTMLTextAreaElement).value;
      resizeTextarea();
    };

    const handleFocus = (event: Event) => {
      emit('focus', event);
    };

    const handleBlur = (event: Event) => {
      emit('blur', event);
    };

    const textarea = ref<Nullable<HTMLTextAreaElement>>(null);
    const focus = () => {
      textarea.value?.focus();
    };
    expose({ focus });

    watch(
      () => internalValue.value,
      async () => {
        if (!props.value.autoResize) {
          return;
        }
        await nextTick();
        resizeTextarea();
      },
    );

    return () => (
      <div
        class={[
          baseClass.value,
          `${baseClass.value}__container`,
          {
            [`${baseClass.value}--error`]: props.value.error,
            [`${baseClass.value}--full-width`]: props.value.fullWidth,
            [`${baseClass.value}--multiline`]: props.value.multiline,
          },
        ]}
      >
        {slots.label?.()
        || (props.value.label && (
          <label class={[`${baseClass.value}__label`]} for={props.value.id}>
            {props.value.label}
          </label>
        ))}
        <div class={`${baseClass.value}__textarea-wrapper`}>
          <textarea
            {...attrs}
            id={props.value.id}
            class={[
              `${baseClass.value}__textarea`,
              { [`${baseClass.value}__textarea--auto-resize`]: props.value.autoResize },
            ]}
            style={props.value.autoResize ? { height: 'auto' } : undefined}
            rows={props.value.autoResize ? DEFAULT_AUTO_RESIZE_ROWS : undefined}
            placeholder={props.value.placeholder}
            value={internalValue.value}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={props.value.disabled}
            maxlength={props.value.maxlength}
            ref={textarea}
          />
        </div>
        {slots.helperText?.()
        || (props.value.helperText && (
          <p class={`${baseClass.value}__helper-text`}>
            {props.value.helperText}
          </p>
        ))}
      </div>
    );
  },
});
